import { PostData } from "./FetchData";

const MS1 = "ms1";
const BASE = "api/v1/website";

function extractMessage(payload, fallback) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message;
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message;
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail;
	}
	return fallback;
}

function parseShopifyResponse(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined || res === null) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "auth_error") {
		return { ok: false, message: extractMessage(res, fallbackMessage), data: null };
	}
	if (
		res.status === false ||
		res.status === "failure" ||
		res.status === "api_error" ||
		res.status === "server_error" ||
		res.status === "application_error" ||
		res.status === "not_found_error"
	) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res.status !== "success" && res.status !== true && res.status !== "connected") {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: null,
		};
	}
	return {
		ok: true,
		message: extractMessage(res, null),
		data: res.data ?? res,
	};
}

function requireWebsiteId(websiteId) {
	if (websiteId == null || websiteId === "") {
		return { ok: false, message: "websiteId is required", data: null };
	}
	return null;
}

/**
 * Normalize shop input to `store.myshopify.com` (strip protocol / path / trailing slash).
 */
export function normalizeShopDomain(value) {
	let s = String(value || "").trim().toLowerCase();
	if (!s) return "";
	s = s.replace(/^https?:\/\//i, "");
	s = s.split("/")[0];
	s = s.replace(/\/$/, "");
	if (s && !s.includes(".") && !s.endsWith(".myshopify.com")) {
		s = `${s}.myshopify.com`;
	}
	return s;
}

/**
 * Start Shopify OAuth. FE should redirect: window.location = installUrl.
 */
export async function startShopifyOAuth({ websiteId, shopDomain }) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const shop = normalizeShopDomain(shopDomain);
	if (!shop) {
		return { ok: false, message: "Shop domain is required", data: null };
	}
	const res = await PostData(MS1, `${BASE}/shopifyStartOAuth`, {
		websiteId: Number(websiteId),
		shopDomain: shop,
	});
	const parsed = parseShopifyResponse(res, "Could not start Shopify OAuth");
	if (!parsed.ok) return parsed;
	const payload = parsed.data || {};
	const installUrl = payload.installUrl || payload.install_url || null;
	if (!installUrl) {
		return {
			ok: false,
			message: "Shopify OAuth did not return an install URL",
			data: payload,
		};
	}
	return {
		ok: true,
		message: parsed.message,
		data: {
			installUrl,
			shopDomain: payload.shopDomain || payload.shop_domain || shop,
		},
	};
}

/**
 * Persist Shopify integration after OAuth (token stays server-side).
 */
export async function saveShopifyIntegration({ websiteId, shopDomain, siteUrl }) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const shop = normalizeShopDomain(shopDomain);
	if (!shop) {
		return { ok: false, message: "Shop domain is required", data: null };
	}
	const res = await PostData(MS1, `${BASE}/shopifySaveIntegration`, {
		websiteId: Number(websiteId),
		shopDomain: shop,
		siteUrl: String(siteUrl || "").trim(),
	});
	const parsed = parseShopifyResponse(res, "Could not save Shopify integration");
	if (!parsed.ok) return parsed;
	return {
		ok: true,
		message: parsed.message || "Shopify integration saved",
		data: parsed.data,
	};
}

/**
 * Test the saved Shopify connection for this website.
 */
export async function testShopifyConnection(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return missing;
	const res = await PostData(MS1, `${BASE}/shopifyTestConnection`, {
		websiteId: Number(websiteId),
	});
	return parseShopifyResponse(res, "Shopify connection test failed");
}

/**
 * Load existing Shopify integration for the current website.
 */
export async function getShopifyIntegration(websiteId) {
	const missing = requireWebsiteId(websiteId);
	if (missing) return { ...missing, connected: false, integration: null };
	const res = await PostData(MS1, `${BASE}/shopifyGetIntegration`, {
		websiteId: Number(websiteId),
	});
	const parsed = parseShopifyResponse(res, "Failed to load Shopify integration");
	if (!parsed.ok) {
		return { ...parsed, connected: false, integration: null };
	}

	const payload = parsed.data;
	const connected = Boolean(payload?.connected);
	const raw = payload?.integration ?? null;

	if (!connected || !raw) {
		return {
			ok: true,
			message: parsed.message,
			connected: false,
			integration: null,
		};
	}

	return {
		ok: true,
		message: parsed.message,
		connected: true,
		integration: {
			id: raw.id ?? null,
			shopDomain:
				raw.shop_domain ||
				raw.shopDomain ||
				payload.shop_domain ||
				payload.shopDomain ||
				"",
			siteUrl: raw.site_url || raw.siteUrl || "",
			createdAt: raw.created_at || raw.createdAt || null,
			updatedAt: raw.updated_at || raw.updatedAt || null,
		},
	};
}
